import axios from "axios";

import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { setPdfValidity, setCertificateId, getCertificateDetails } from "../../../../actions/certificate_validator";
import { startValidating, stopValidating } from "../../../../actions/loading";
import { extractCertificateIdFromPdf } from "../../../../utils/pdfLibUtils";
import { getBackendUrl } from "../../../../utils/routes";

type TVerifyPdfResponse = {
    verified: boolean;
    additionalAnnotations: boolean;
    authenticity: boolean;
    current: boolean;
    integrity: boolean;
    signatureField: boolean;
    meta: {
        reason: string;
        date: string;
        certificateId: string | null;
    };
};

const getVerifyPdfFunctionUrl = (environment: TEnv) => {
    switch (environment) {
        case "local":
        case "development":
            return "https://dev.functions-n.virtualbadge.io/api/verifyPdf";
        case "staging":
            return "https://stage.functions-n.virtualbadge.io/api/verifyPdf";
        case "prod":
        default:
            return "https://functions-n.virtualbadge.io/api/verifyPdf";
    }
};

export const usePdfValidation = (
    environment: TEnv,
    api_identifier: string,
    brandName: TAccountType,
    providedSigningCertificateName?: string
) => {
    const dispatch = useAppDispatch();
    const certificateDetails = useAppSelector((state) => state.certificate_validator.certificateDetails);
    const loading = useAppSelector((state) => state.loading.isValidating);

    const validatePdf = useCallback(
        async (pdfFile: File) => {
            dispatch(startValidating());

            const validatePdfWithCertificate = async (pdfFile: File, certName: string) => {
                const formData = new FormData();
                formData.append("pdf_file", pdfFile);
                formData.append("cert_name", certName);

                try {
                    const response = await axios.post<TVerifyPdfResponse>(
                        getVerifyPdfFunctionUrl(environment),
                        formData
                    );
                    const { verified, meta } = response.data;
                    const { certificateId } = meta;

                    // When using provided signing certificate, validate that certificate IDs match
                    if (providedSigningCertificateName && certificateDetails) {
                        if (verified && certificateId && certificateId !== certificateDetails.certificate_id) {
                            dispatch(setPdfValidity("invalid_id"));
                            return;
                        }
                    }

                    dispatch(setPdfValidity(verified ? "valid" : "invalid"));
                } catch (error) {
                    dispatch(setPdfValidity("invalid"));
                }
            };

            try {
                // If a signing certificate name is provided, validate directly
                if (providedSigningCertificateName) {
                    await validatePdfWithCertificate(pdfFile, providedSigningCertificateName);
                    return;
                }

                // Original drag-and-drop flow
                const extractedCertificateId = await extractCertificateIdFromPdf(pdfFile);

                // If no certificate ID found, show error - user must enter ID manually
                if (!extractedCertificateId) {
                    return { error: "certificate-id-missing" } as const;
                }

                // First, check if we already have certificate details in state for this certificate ID
                let signingCertificateName = certificateDetails?.key_vault_certificate_name;

                // If we don't have the certificate details or it's for a different certificate, fetch new data
                if (!certificateDetails || certificateDetails.certificate_id !== extractedCertificateId) {
                    // Fetch certificate details directly using axios to get the response
                    const API_URL = getBackendUrl(environment, brandName);
                    const params = new URLSearchParams({
                        identifier: api_identifier,
                        rat: brandName,
                        certificate_id: extractedCertificateId,
                    });
                    const validationUrl = `${API_URL}/api/public/certificate_validation?${params.toString()}`;

                    const response = await axios.get<ICertificateDetails>(validationUrl);
                    const freshCertificateDetails = response.data;

                    // Dispatch actions to update the store
                    await dispatch(
                        getCertificateDetails(extractedCertificateId, api_identifier, environment, brandName) as any
                    );

                    signingCertificateName = freshCertificateDetails.key_vault_certificate_name;
                }

                dispatch(setCertificateId(extractedCertificateId));

                // Use the signing certificate name if we have it
                if (signingCertificateName) {
                    await validatePdfWithCertificate(pdfFile, signingCertificateName);
                }
                // We don't need to do anything if no signing certificate is configured.
            } catch (err) {
                return { error: "unknown" } as const; // Shows the fallback error notification
            } finally {
                dispatch(stopValidating());
            }
        },
        [dispatch, certificateDetails, environment, api_identifier, brandName, providedSigningCertificateName]
    );

    return {
        validatePdf,
        loading,
    };
};
